import {
  ApiEmployeeColumn,
  ApiEmployeeColumnFilter,
  EmployeeColumnEntity,
  EmployeeColumnFilterCategory,
  SYSTEM_TABLE_TYPE_BY_ENTITY,
} from "@/types/employees/employee-column.types.ts";
import { LIFECYCLE_STATUS_COLUMN } from "@/pages/employees/index/use-employee-column-filters.ts";
import type { ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";

/**
 * Черновик панели. Пока статус не задан явно, список ограничен статусами
 * организации по умолчанию: панель показывает их фильтром по статусу, чтобы
 * их было видно и можно было снять. Без колонки статуса в каталоге (нет права)
 * бэк всё равно подставит значения по умолчанию — показывать нечего.
 */
export const seedLifecycleStatusFilter = (
  filters: ApiEmployeeColumnFilter[],
  lifecycleColumn: ApiEmployeeColumn | undefined,
  defaultStatuses: ApiEmployeeLifecycleStatus[] | undefined
): ApiEmployeeColumnFilter[] => {
  if (!lifecycleColumn?.is_filterable || !defaultStatuses?.length) return filters;
  if (filters.some((filter) => filter.field === LIFECYCLE_STATUS_COLUMN)) return filters;
  return [{ field: LIFECYCLE_STATUS_COLUMN, option_ids: [...defaultStatuses] }, ...filters];
};

/**
 * Фильтры для счётчика на кнопке и чипа в поиске. Статусы организации по
 * умолчанию сужают список, пока статус не задан явно, — значит, это действующий
 * фильтр. Явное «все статусы» (`[]`) ничего не сужает и не показывается.
 */
export const getDisplayedColumnFilters = (
  activeFilters: ApiEmployeeColumnFilter[],
  lifecycleColumn: ApiEmployeeColumn | undefined,
  defaultStatuses: ApiEmployeeLifecycleStatus[] | undefined
): ApiEmployeeColumnFilter[] => {
  const lifecycle = activeFilters.find((filter) => filter.field === LIFECYCLE_STATUS_COLUMN);
  if (lifecycle) {
    return lifecycle.option_ids?.length
      ? activeFilters
      : activeFilters.filter((filter) => filter !== lifecycle);
  }
  return seedLifecycleStatusFilter(activeFilters, lifecycleColumn, defaultStatuses);
};

/**
 * Набор после «Сбросить»: без статуса бэк снова подставил бы статусы
 * организации, поэтому сброс задаёт «все статусы» явно — как сброс старой
 * панели (`keepEmptyArrayInUrl`).
 */
export const getClearedColumnFilters = (
  lifecycleColumn: ApiEmployeeColumn | undefined,
  defaultStatuses: ApiEmployeeLifecycleStatus[] | undefined
): ApiEmployeeColumnFilter[] =>
  lifecycleColumn?.is_filterable && defaultStatuses?.length
    ? [{ field: LIFECYCLE_STATUS_COLUMN, option_ids: [] }]
    : [];

/**
 * Новый фильтр без значения: условие по умолчанию — сравнение со значением, у
 * bool условие выбирается явно. Пустой фильтр в запрос не уходит.
 */
export const createEmployeeColumnFilter = (column: ApiEmployeeColumn): ApiEmployeeColumnFilter => ({
  field: column.id,
});

/**
 * Название таблицы для колонки: у системной таблицы (позиция, занятость,
 * компенсация, увольнение, разовые выплаты) — название её группы, у кастомной —
 * название самой таблицы. У личных данных и кастомных полей профиля таблицы нет.
 */
export const getFilterColumnTableTitles = (
  categories: EmployeeColumnFilterCategory[]
): Map<string, string> => {
  const titles = new Map<string, string>();
  for (const category of categories) {
    const isSystemTable = category.id in SYSTEM_TABLE_TYPE_BY_ENTITY;
    const isCustomTable = category.id === EmployeeColumnEntity.custom_table;
    if (!isSystemTable && !isCustomTable) continue;
    for (const column of category.columns) {
      titles.set(column.id, (isCustomTable && column.groupTitle) || category.title);
    }
  }
  return titles;
};

/** Подпись поля фильтра: у поля таблицы после названия идёт название таблицы. */
export const getFilterFieldTitle = (title: string, tableTitle?: string): string =>
  tableTitle ? `${title} - ${tableTitle}` : title;

export const getAvailableFilterCategories = (
  categories: EmployeeColumnFilterCategory[],
  selectedFields: Set<string>,
  searchTerm: string
): EmployeeColumnFilterCategory[] => {
  const term = searchTerm.trim().toLowerCase();
  return categories
    .map((category) => ({
      ...category,
      columns: category.columns.filter(
        (column) =>
          !selectedFields.has(column.id) &&
          (!term ||
            category.title.toLowerCase().includes(term) ||
            column.groupTitle?.toLowerCase().includes(term) ||
            column.title.toLowerCase().includes(term))
      ),
    }))
    .filter((category) => category.columns.length > 0);
};

export const reorderEmployeeColumnFilters = (
  filters: ApiEmployeeColumnFilter[],
  sourceIndex: number,
  destinationIndex: number
): ApiEmployeeColumnFilter[] => {
  const reordered = [...filters];
  const [moved] = reordered.splice(sourceIndex, 1);
  reordered.splice(destinationIndex, 0, moved);
  return reordered;
};
